import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Plus, AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api.ts';
import {
  type FieldDef,
  type Rule,
  type SubGroup,
  type BuilderState,
  STANDARD_FIELDS,
  buildDsl,
  parseDsl,
  emptyState,
  makeSubGroup,
} from './dsl-utils.ts';
import { RuleGroup, SubGroupComponent } from './RuleGroup.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PropertyDefinition {
  key: string;
  label: string;
  fieldType: string;
  options: string[];
  isActive: boolean;
}

interface Props {
  /** Current DSL value (the JSON stored in definitionJson) */
  value: Record<string, unknown>;
  /** Called on every change with the updated DSL */
  onChange: (dsl: Record<string, unknown>) => void;
}

// ─── Helper: cast PropertyDefinition fieldType to FieldDef fieldType ─────────

function toFieldType(raw: string): FieldDef['fieldType'] {
  const map: Record<string, FieldDef['fieldType']> = {
    text: 'text',
    number: 'number',
    boolean: 'boolean',
    date: 'date',
    email: 'email',
    url: 'url',
    select: 'select',
    multiselect: 'multiselect',
  };
  return map[raw] ?? 'text';
}

// ─── SegmentBuilder ───────────────────────────────────────────────────────────

export function SegmentBuilder({ value, onChange }: Props) {
  // ── Load property definitions ──────────────────────────────────────────────
  const { data: propDefs = [] } = useQuery<PropertyDefinition[]>({
    queryKey: ['property-definitions-active'],
    queryFn: () =>
      api
        .get('/api/v1/settings/properties')
        .then((r) => (r.data as PropertyDefinition[]).filter((p) => p.isActive)),
    staleTime: 60_000,
  });

  // Combine standard + custom fields
  const fields: FieldDef[] = [
    ...STANDARD_FIELDS,
    ...propDefs.map((p) => ({
      key: p.key,
      label: p.label,
      fieldType: toFieldType(p.fieldType),
      options: p.options ?? [],
      isStandard: false,
    })),
  ];

  // ── Determine initial mode ─────────────────────────────────────────────────
  const [mode, setMode] = useState<'visual' | 'json'>('visual');
  const [state, setState] = useState<BuilderState>(emptyState);
  const [jsonText, setJsonText] = useState('{}');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [dslOpen, setDslOpen] = useState(false);

  // Parse incoming value once on mount (or when value reference changes deeply)
  useEffect(() => {
    const result = parseDsl(value);
    if (result.ok) {
      setMode('visual');
      setState(result.state);
    } else {
      setMode('json');
      setJsonText(JSON.stringify(value, null, 2));
    }
    // We intentionally do NOT re-run on every `value` change to avoid
    // overwriting mid-edit state. The consumer should only pass a stable
    // initial value, and subsequent changes come from our own callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Emit DSL on every state change (visual mode) ───────────────────────────
  const emitFromState = useCallback(
    (s: BuilderState) => {
      onChange(buildDsl(s));
    },
    [onChange],
  );

  const updateState = (next: BuilderState) => {
    setState(next);
    emitFromState(next);
  };

  // ── Root logic ────────────────────────────────────────────────────────────
  const rootRules = state.items.filter((i): i is Rule => !('kind' in i));
  const subGroups = state.items.filter((i): i is SubGroup => 'kind' in i);

  const setRootRules = (rules: Rule[]) => {
    updateState({ ...state, items: [...rules, ...subGroups] });
  };

  const setSubGroups = (groups: SubGroup[]) => {
    updateState({ ...state, items: [...rootRules, ...groups] });
  };

  const addSubGroup = () => {
    if (subGroups.length >= 5) return; // reasonable upper bound
    const group = makeSubGroup();
    updateState({ ...state, items: [...state.items, group] });
  };

  const updateSubGroup = (idx: number, group: SubGroup) => {
    const next = [...subGroups];
    next[idx] = group;
    setSubGroups(next);
  };

  const removeSubGroup = (idx: number) => {
    setSubGroups(subGroups.filter((_, i) => i !== idx));
  };

  // ── JSON mode ──────────────────────────────────────────────────────────────
  const handleJsonChange = (text: string) => {
    setJsonText(text);
    try {
      const parsed = JSON.parse(text);
      setJsonError(null);
      onChange(parsed);
    } catch {
      setJsonError('Invalid JSON');
    }
  };

  const trySwitchToVisual = () => {
    try {
      const parsed = JSON.parse(jsonText);
      const result = parseDsl(parsed);
      if (result.ok) {
        setState(result.state);
        setMode('visual');
        setJsonError(null);
      } else {
        setJsonError('Cannot parse this DSL in the visual builder');
      }
    } catch {
      setJsonError('Invalid JSON');
    }
  };

  // ── Computed DSL preview ───────────────────────────────────────────────────
  const previewDsl = mode === 'visual' ? JSON.stringify(buildDsl(state), null, 2) : jsonText;

  // ─── Render: JSON fallback mode ─────────────────────────────────────────────
  if (mode === 'json') {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs text-amber-600">
            <AlertTriangle size={13} />
            <span>Advanced DSL — editing as JSON</span>
          </div>
          <button
            type="button"
            onClick={trySwitchToVisual}
            className="text-xs text-zinc-500 underline hover:text-zinc-800 transition-colors"
          >
            Try visual builder
          </button>
        </div>
        <textarea
          className="w-full bg-white border border-zinc-200 rounded-lg text-xs font-mono px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none"
          rows={8}
          value={jsonText}
          onChange={(e) => handleJsonChange(e.target.value)}
          spellCheck={false}
        />
        {jsonError && <p className="text-xs text-red-500">{jsonError}</p>}
      </div>
    );
  }

  // ─── Render: visual mode ────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {/* Root rules group */}
      <RuleGroup
        logic={state.logic}
        rules={rootRules}
        fields={fields}
        onLogicChange={(v) => updateState({ ...state, logic: v })}
        onRulesChange={setRootRules}
        isRoot
      />

      {/* Sub-groups */}
      {subGroups.map((group, idx) => (
        <SubGroupComponent
          key={group.id}
          group={group}
          fields={fields}
          onChange={(g) => updateSubGroup(idx, g)}
          onRemove={() => removeSubGroup(idx)}
        />
      ))}

      {/* Add group button */}
      <button
        type="button"
        onClick={addSubGroup}
        className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-700 border border-dashed border-zinc-200 hover:border-zinc-300 rounded-lg px-3 py-1.5 transition-colors w-full justify-center"
      >
        <Plus size={12} />
        Add group
      </button>

      {/* DSL preview — collapsible */}
      <div className="border border-zinc-100 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setDslOpen((o) => !o)}
          className="flex items-center justify-between w-full px-3 py-2 text-xs text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50 transition-colors"
        >
          <span>Preview DSL</span>
          {dslOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
        {dslOpen && (
          <pre className="text-[11px] font-mono text-zinc-600 bg-zinc-50 px-3 py-2.5 overflow-auto max-h-48 border-t border-zinc-100">
            {previewDsl}
          </pre>
        )}
      </div>
    </div>
  );
}
